/**
 * <LegalEntitySwitcher /> — top-bar Legal Entity scope switcher.
 * Rebuilt M27 PR 3 after Amer's user-test: "the company name
 * appears but isn't clickable to switch."
 *
 * Per BUILD_READINESS_M27.md §6 acceptance #26:
 *   - Audit + fix the existing component.
 *   - Dropdown shows: list of all LegalEntities + "All entities"
 *     option + checkmark on currently active scope.
 *   - Persistence: user's selection saved per-user.
 *   - 1-LE tenant: shows just the name (no dropdown needed but
 *     visually consistent).
 *   - N-LE tenant: shows the active LE + chevron, click reveals
 *     full picker with checkmark.
 *
 * Implementation note: replaces the prior native <select>, which
 * worked but didn't read as clickable. The new version uses an
 * accessible button + popover pattern (close on outside click +
 * Escape, focus-trapped).
 */

import {
  Building2,
  Check,
  ChevronDown,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ALL_ENTITIES, useOptionalLegalEntityContext } from "../lib/legalEntity";

export function LegalEntitySwitcher() {
  const { t } = useTranslation();
  const ctx = useOptionalLegalEntityContext();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (ctx === null) return null;
  const { legalEntities, scope, setScope, isLoading } = ctx;
  if (isLoading) return null;

  const activeLabel =
    scope === ALL_ENTITIES
      ? t("app.le_switcher_all_entities")
      : legalEntities.find((e) => e.id === scope)?.name_en ?? "—";

  // Single LE: read-only pill (consistent visual; not clickable).
  if (legalEntities.length <= 1) {
    const only = legalEntities[0];
    return (
      <span
        className="hidden md:inline-flex items-center gap-1.5 rounded-md border border-border-subtle bg-surface-default px-2.5 py-1 text-xs font-medium"
        title={only?.name_ar ?? undefined}
      >
        <Building2 size={12} aria-hidden className="text-text-tertiary" />
        <span className="text-text-primary">{only?.name_en ?? "—"}</span>
      </span>
    );
  }

  // N LEs: clickable button → popover with the full list.
  return (
    <div ref={rootRef} className="relative hidden md:inline-flex">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${t("app.le_switcher_aria_prefix")} ${activeLabel}`}
        className={[
          "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors",
          open
            ? "border-brand bg-surface-sunken text-text-primary"
            : "border-border-subtle bg-surface-default text-text-primary hover:bg-surface-sunken",
        ].join(" ")}
      >
        <Building2 size={12} aria-hidden className="text-text-tertiary" />
        <span className="max-w-[120px] truncate">{activeLabel}</span>
        <ChevronDown
          size={12}
          aria-hidden
          className={`text-text-tertiary transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={t("app.le_switcher_listbox_label")}
          // Explicit opaque bg — same belt-and-braces fix as the AI
          // status popover so the popover never reads transparent.
          style={{ backgroundColor: "var(--surface-default, #ffffff)" }}
          className="absolute end-0 top-full z-50 mt-1 w-64 overflow-hidden rounded-lg border border-border-subtle shadow-lg"
        >
          <ul className="max-h-80 overflow-auto p-1">
            <li>
              <ScopeRow
                label={t("app.le_switcher_all_entities")}
                hint={t("app.le_switcher_all_hint", { n: legalEntities.length })}
                selected={scope === ALL_ENTITIES}
                onPick={() => {
                  setScope(ALL_ENTITIES);
                  setOpen(false);
                }}
              />
            </li>
            <li
              role="separator"
              aria-hidden
              className="my-1 border-t border-border-subtle"
            />
            {legalEntities.map((e) => (
              <li key={e.id}>
                <ScopeRow
                  label={e.name_en}
                  hint={e.name_ar ?? ""}
                  hintDir="rtl"
                  selected={scope === e.id}
                  onPick={() => {
                    setScope(e.id);
                    setOpen(false);
                  }}
                />
              </li>
            ))}
          </ul>
          <footer className="border-t border-border-subtle bg-surface-sunken px-3 py-2 text-xs text-text-tertiary">
            {t("app.le_switcher_saved_note")}
          </footer>
        </div>
      )}
    </div>
  );
}

function ScopeRow({
  label,
  hint,
  hintDir,
  selected,
  onPick,
}: {
  label: string;
  hint?: string;
  hintDir?: "ltr" | "rtl";
  selected: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onPick}
      className={[
        "flex w-full items-start gap-2 rounded px-2 py-1.5 text-start text-sm transition-colors",
        selected
          ? "bg-ai-bg text-text-primary"
          : "text-text-secondary hover:bg-surface-sunken hover:text-text-primary",
      ].join(" ")}
    >
      <span className="flex h-4 w-4 shrink-0 items-center justify-center">
        {selected && <Check size={12} className="text-brand" aria-hidden />}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block truncate font-medium">{label}</span>
        {hint && (
          <span
            dir={hintDir}
            className="block truncate text-xs text-text-tertiary"
          >
            {hint}
          </span>
        )}
      </span>
    </button>
  );
}
